import { ChevronDown, ChevronRight, Copy, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const SlotIconsEditor = ({ slotIcons, onUpdate, slotId }) => {
  const [localSlotIcons, setLocalSlotIcons] = useState(slotIcons);
  const [expandedIcons, setExpandedIcons] = useState({});
  // const [editingCombo, setEditingCombo] = useState(null);

  const allPossibleSymbols = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'SE1',
    'SE2',
    'SC1',
    'SC2',
    'HP',
    'LP',
    'WL',
    'ML',
    'SE',
  ];
  const existingSymbols = localSlotIcons.map((icon) => icon.icon_id);
  const availableSymbols = [...new Set([...existingSymbols, ...allPossibleSymbols])].sort();

  const unusedSymbols = allPossibleSymbols.filter((symbol) => !localSlotIcons.some((icon) => icon.icon_id === symbol));

  const rewardTypes = ['COINS', 'BONUS_COINS', 'BONUS'];
  const payoffTypes = ['LTR', 'RTL', 'ANY'];
  const comboTypes = ['X_OF_KIND', 'SCATTER', 'LINE'];

  // Debounced update
  useEffect(() => {
    const timer = setTimeout(() => {
      onUpdate(localSlotIcons);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSlotIcons, onUpdate]);

  // Додати нову іконку
  const addSlotIcon = () => {
    const newIconId = unusedSymbols.length > 0 ? unusedSymbols[0] : `NEW_${Date.now()}`;

    const newIcon = {
      icon_id: newIconId,
      slot_id: slotId,
      combos: [
        {
          pattern: 'x8',
          type: 'X_OF_KIND',
          rewards: [
            {
              iconId: newIconId,
              type: 'COINS',
              multiplier: 25,
            },
          ],
          payoffType: 'LTR',
        },
      ],
    };
    setLocalSlotIcons((prev) => [...prev, newIcon]);
  };

  // Видалити іконку
  const removeSlotIcon = (index) => {
    setLocalSlotIcons((prev) => prev.filter((_, i) => i !== index));
  };

  // Оновити іконку
  const updateSlotIcon = (index, updates) => {
    setLocalSlotIcons((prev) => prev.map((icon, i) => (i === index ? { ...icon, ...updates } : icon)));
  };

  // Додати combo до іконки
  const addCombo = (iconIndex) => {
    const newCombo = {
      pattern: 'x1',
      type: 'X_OF_KIND',
      rewards: [
        {
          iconId: localSlotIcons[iconIndex].icon_id,
          type: 'COINS',
          multiplier: 25,
        },
      ],
      payoffType: 'LTR',
    };

    setLocalSlotIcons((prev) =>
      prev.map((icon, i) => (i === iconIndex ? { ...icon, combos: [...icon.combos, newCombo] } : icon)),
    );
  };

  // Видалити combo
  const removeCombo = (iconIndex, comboIndex) => {
    setLocalSlotIcons((prev) =>
      prev.map((icon, i) =>
        i === iconIndex
          ? {
              ...icon,
              combos: icon.combos.filter((_, ci) => ci !== comboIndex),
            }
          : icon,
      ),
    );
  };

  // Оновити combo
  const updateCombo = (iconIndex, comboIndex, updates) => {
    setLocalSlotIcons((prev) =>
      prev.map((icon, i) =>
        i === iconIndex
          ? {
              ...icon,
              combos: icon.combos.map((combo, ci) => (ci === comboIndex ? { ...combo, ...updates } : combo)),
            }
          : icon,
      ),
    );
  };

  // Оновити reward
  const updateReward = (iconIndex, comboIndex, rewardIndex, updates) => {
    setLocalSlotIcons((prev) =>
      prev.map((icon, i) =>
        i === iconIndex
          ? {
              ...icon,
              combos: icon.combos.map((combo, ci) =>
                ci === comboIndex
                  ? {
                      ...combo,
                      rewards: combo.rewards.map((reward, ri) =>
                        ri === rewardIndex ? { ...reward, ...updates } : reward,
                      ),
                    }
                  : combo,
              ),
            }
          : icon,
      ),
    );
  };

  // Додати reward до combo
  const addReward = (iconIndex, comboIndex) => {
    const newReward = {
      iconId: localSlotIcons[iconIndex].icon_id,
      type: 'COINS',
      multiplier: 25,
    };

    setLocalSlotIcons((prev) =>
      prev.map((icon, i) =>
        i === iconIndex
          ? {
              ...icon,
              combos: icon.combos.map((combo, ci) =>
                ci === comboIndex ? { ...combo, rewards: [...combo.rewards, newReward] } : combo,
              ),
            }
          : icon,
      ),
    );
  };

  // Генерувати range combos
  const generateRangeCombos = (iconIndex, startPattern, endPattern, baseMultiplier, multiplierType) => {
    const start = parseInt(startPattern.replace('x', ''));
    const end = parseInt(endPattern.replace('x', ''));

    const newCombos = [];
    for (let i = start; i <= end; i++) {
      let multiplier;
      if (multiplierType === 'linear') {
        multiplier = baseMultiplier * i;
      } else if (multiplierType === 'fixed') {
        multiplier = baseMultiplier;
      } else {
        // incremental
        multiplier = baseMultiplier + (i - start) * 25;
      }

      newCombos.push({
        pattern: `x${i}`,
        type: 'X_OF_KIND',
        rewards: [
          {
            iconId: localSlotIcons[iconIndex].icon_id,
            type: 'COINS',
            multiplier: multiplier,
          },
        ],
        payoffType: 'LTR',
      });
    }

    setLocalSlotIcons((prev) =>
      prev.map((icon, i) => (i === iconIndex ? { ...icon, combos: [...icon.combos, ...newCombos] } : icon)),
    );
  };

  // Дублювати іконку
  const duplicateIcon = (index) => {
    const icon = localSlotIcons[index];
    const duplicatedIcon = {
      ...icon,
      icon_id: `${icon.icon_id}_COPY_${Date.now()}`,
      combos: icon.combos.map((combo) => ({
        ...combo,
        rewards: combo.rewards.map((reward) => ({
          ...reward,
          iconId: `${icon.icon_id}_COPY_${Date.now()}`,
        })),
      })),
    };
    setLocalSlotIcons((prev) => [...prev, duplicatedIcon]);
  };

  const toggleIconExpanded = (index) => {
    setExpandedIcons((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="p-4 bg-white border border-gray-300 rounded-lg">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Slot Icons Configuration</h3>
        <div className="flex gap-2">
          <button
            onClick={addSlotIcon}
            className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
          >
            <Plus className="w-4 h-4" />
            Додати іконку
          </button>
        </div>
      </div>

      {/* Список іконок */}
      <div className="space-y-4">
        {localSlotIcons.map((icon, iconIndex) => (
          <div key={iconIndex} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            {/* Заголовок іконки */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button onClick={() => toggleIconExpanded(iconIndex)} className="p-1 rounded hover:bg-gray-200">
                  {expandedIcons[iconIndex] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <select
                  value={icon.icon_id}
                  onChange={(e) => {
                    const newIconId = e.target.value;
                    const updatedIcon = {
                      ...icon,
                      icon_id: newIconId,
                      combos: icon.combos.map((combo) => ({
                        ...combo,
                        rewards: combo.rewards.map((reward) => ({
                          ...reward,
                          iconId: reward.type !== 'BONUS' ? newIconId : reward.iconId,
                        })),
                      })),
                    };
                    setLocalSlotIcons((prev) => prev.map((ic, i) => (i === iconIndex ? updatedIcon : ic)));
                  }}
                  className="px-2 py-1 text-lg font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {availableSymbols.map((symbol) => (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600">({icon.combos.length} combos)</span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => duplicateIcon(iconIndex)}
                  className="p-1 text-blue-600 bg-blue-100 rounded hover:bg-blue-200"
                  title="Дублювати"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeSlotIcon(iconIndex)}
                  className="p-1 text-red-600 bg-red-100 rounded hover:bg-red-200"
                  title="Видалити"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Розгорнутий вміст */}
            {expandedIcons[iconIndex] && (
              <div className="space-y-4">
                {/* Генератор range */}
                <div className="p-3 border border-blue-200 rounded bg-blue-50">
                  <h4 className="mb-2 text-sm font-medium text-blue-800">Генератор Range Combos</h4>
                  <div className="grid grid-cols-6 gap-2 text-sm">
                    <div className="px-2 py-1">
                      <label className="block mb-1 text-sm font-medium text-gray-700">From</label>
                      <input type="text" placeholder="1" className="border rounded" id={`start-${iconIndex}`} />
                    </div>
                    <div className="px-2 py-1">
                      <label className="block mb-1 text-sm font-medium text-gray-700">To</label>
                      <input type="text" placeholder="30" className="border rounded" id={`end-${iconIndex}`} />
                    </div>
                    <div className="px-2 py-1">
                      <label className="block mb-1 text-sm font-medium text-gray-700">Multiplier</label>
                      <input type="number" placeholder="25" className="border rounded" id={`multiplier-${iconIndex}`} />
                    </div>

                    <select className="self-end px-2 py-1 text-sm border rounded" id={`type-${iconIndex}`}>
                      <option value="linear">Linear</option>
                      <option value="fixed">Fixed</option>
                      <option value="incremental">Incremental</option>
                    </select>
                    <button
                      onClick={() => {
                        const start = `x${document.getElementById(`start-${iconIndex}`).value || '1'}`;
                        const end = `x${document.getElementById(`end-${iconIndex}`).value || '30'}`;
                        const mult = parseInt(document.getElementById(`multiplier-${iconIndex}`).value) || 25;
                        const type = document.getElementById(`type-${iconIndex}`).value;
                        generateRangeCombos(iconIndex, start, end, mult, type);
                      }}
                      className="self-end px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                    >
                      Генерувати
                    </button>
                    <button
                      onClick={() => updateSlotIcon(iconIndex, { combos: [] })}
                      className="self-end px-3 py-1 text-sm text-white bg-red-500 rounded hover:bg-red-600"
                    >
                      Очистити
                    </button>
                  </div>
                </div>

                {/* Combos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Combos</h4>
                    <button
                      onClick={() => addCombo(iconIndex)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 bg-green-100 rounded hover:bg-green-200"
                    >
                      <Plus className="w-3 h-3" />
                      Combo
                    </button>
                  </div>

                  <div className="space-y-2 overflow-y-auto max-h-64">
                    {icon.combos.map((combo, comboIndex) => (
                      <div key={comboIndex} className="p-3 bg-white border rounded">
                        <div className="grid items-center grid-cols-6 gap-2 mb-2">
                          <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-gray-500">
                              x
                            </span>
                            <input
                              type="number"
                              value={combo.pattern.replace('x', '')}
                              onChange={(e) =>
                                updateCombo(iconIndex, comboIndex, {
                                  pattern: `x${e.target.value}`,
                                })
                              }
                              className="w-full px-2 py-1 pl-4 text-xs border rounded"
                              placeholder="1"
                              min="1"
                            />
                          </div>
                          <select
                            value={combo.type}
                            onChange={(e) =>
                              updateCombo(iconIndex, comboIndex, {
                                type: e.target.value,
                              })
                            }
                            className="px-2 py-1 text-xs border rounded"
                          >
                            {comboTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                          <select
                            value={combo.payoffType}
                            onChange={(e) =>
                              updateCombo(iconIndex, comboIndex, {
                                payoffType: e.target.value,
                              })
                            }
                            className="px-2 py-1 text-xs border rounded"
                          >
                            {payoffTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                          <span className="text-xs text-gray-600">{combo.rewards.length} rewards</span>
                          <button
                            onClick={() => addReward(iconIndex, comboIndex)}
                            className="px-2 py-1 text-xs text-blue-600 bg-blue-100 rounded hover:bg-blue-200"
                          >
                            + Reward
                          </button>
                          <button
                            onClick={() => removeCombo(iconIndex, comboIndex)}
                            className="flex items-center justify-center gap-1 px-2 py-1 text-xs text-red-600 bg-red-100 rounded col hover:bg-red-200"
                          >
                            Remove
                          </button>
                        </div>

                        {/* Rewards */}
                        <div className="space-y-1">
                          {combo.rewards.map((reward, rewardIndex) => (
                            <div
                              key={rewardIndex}
                              className="grid items-center grid-cols-5 gap-2 p-2 rounded bg-gray-50"
                            >
                              <select
                                value={reward.type}
                                onChange={(e) =>
                                  updateReward(iconIndex, comboIndex, rewardIndex, { type: e.target.value })
                                }
                                className="px-2 py-1 text-xs border rounded"
                              >
                                {rewardTypes.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                value={reward.multiplier || ''}
                                onChange={(e) =>
                                  updateReward(iconIndex, comboIndex, rewardIndex, {
                                    multiplier: parseInt(e.target.value) || 0,
                                  })
                                }
                                className="px-2 py-1 text-xs border rounded"
                                placeholder="Multiplier"
                              />
                              <input
                                type="text"
                                value={reward.id || ''}
                                onChange={(e) =>
                                  updateReward(iconIndex, comboIndex, rewardIndex, { id: e.target.value })
                                }
                                className="px-2 py-1 text-xs border rounded"
                                placeholder="Bonus ID"
                              />
                              <button
                                onClick={() => {
                                  const updatedRewards = combo.rewards.filter((_, ri) => ri !== rewardIndex);
                                  updateCombo(iconIndex, comboIndex, {
                                    rewards: updatedRewards,
                                  });
                                }}
                                className="flex items-center justify-center col-start-5 gap-1 px-2 py-1 text-xs text-red-600 bg-red-100 rounded hover:bg-red-200"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Статистика */}
      <div className="p-3 mt-4 text-sm text-gray-600 bg-gray-100 rounded">
        Загально іконок: {localSlotIcons.length} | Загально combos:{' '}
        {localSlotIcons.reduce((acc, icon) => acc + icon.combos.length, 0)} | Slot ID: {slotId}
      </div>
    </div>
  );
};

export default SlotIconsEditor;
